"use client";

/**
 * A record: BB release artwork in a circle, one turn every 3.6 s (half of
 * 33⅓ rpm — Gordon: "spinning too fast, half the speed"), the next release
 * fading in over 4 s after a 1 s hold — a new cover every 5 s (Gordon:
 * "shorter interval between spinning slideshow images and longer fades").
 * Grooves and a fixed light sheen sit on top so the turning reads as vinyl.
 * Reduced-motion users get the record still.
 */

import { useEffect, useState } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";

// Shared with the manifesto slideshow so every image on /launch changes in step.
export const HOLD_MS = 1000;
export const FADE_MS = 4000;

/** Two stacked layers: the visible image, and the next one fading in on top.
 *  Returns which image is showing and whether the next is fading in. */
export function useImageCycle(count: number, offset = 0, holdMs = HOLD_MS, fadeMs = FADE_MS) {
  const [index, setIndex] = useState(offset);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (count < 2) return;
    const timer = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setIndex(i => (i + 1) % count);
        setFading(false);
      }, fadeMs);
    }, holdMs + fadeMs);
    return () => clearInterval(timer);
  }, [count, holdMs, fadeMs]);

  return { index, fading };
}

export function VinylDisc({ covers, offset = 0, className, holdMs = HOLD_MS, fadeMs = FADE_MS }: { covers: string[]; offset?: number; className?: string; holdMs?: number; fadeMs?: number }) {
  const { index, fading } = useImageCycle(covers.length, offset, holdMs, fadeMs);

  if (covers.length === 0) {
    return <div className={cn("aspect-square rounded-full bg-[var(--launch-accent)]", className)} aria-hidden />;
  }
  const current = covers[index % covers.length];
  const next = covers[(index + 1) % covers.length];

  return (
    <div className={cn("relative aspect-square shrink-0", className)} aria-hidden>
      <div className="bb-vinyl-spin absolute inset-0 overflow-hidden rounded-full bg-black shadow-2xl">
        <Image src={current} alt="" fill sizes="(min-width: 1024px) 45vw, 85vw" className="object-cover" />
        <Image
          src={next}
          alt=""
          fill
          sizes="(min-width: 1024px) 45vw, 85vw"
          // Fades in over fadeMs; the reset afterwards is instant (the layer
          // underneath already shows the same cover), so nothing fades back.
          className={cn("object-cover ease-in-out", fading ? "opacity-100 transition-opacity" : "opacity-0 transition-none")}
          style={{ transitionDuration: fading ? `${fadeMs}ms` : "0ms" }}
        />
        {/* Grooves */}
        <div className="absolute inset-0 rounded-full bg-[repeating-radial-gradient(circle_at_center,rgba(0,0,0,0.16)_0px,rgba(0,0,0,0.16)_1px,transparent_1px,transparent_5px)]" />
      </div>
      {/* Light that stays put while the record turns underneath it */}
      <div className="pointer-events-none absolute inset-0 rounded-full bg-[conic-gradient(from_200deg,transparent_0deg,rgba(255,255,255,0.18)_30deg,transparent_70deg,transparent_180deg,rgba(255,255,255,0.12)_210deg,transparent_250deg)]" />
      {/* Spindle hole */}
      <div className="absolute left-1/2 top-1/2 h-[4%] w-[4%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--launch-bg)] ring-2 ring-white/20" />
    </div>
  );
}
